import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./lib/auth";
import { Login, Join } from "./screens/Auth";
import { Onboarding } from "./screens/Onboarding";
import { Landing } from "./screens/Landing";
import { CrewLayout, TabbedLayout } from "./screens/CrewLayout";
import { You } from "./screens/You";
import { MyQuestions } from "./screens/MyQuestions";
import { Home } from "./screens/Home";
import { CheckIn } from "./screens/CheckIn";
import { Candidates } from "./screens/Candidates";
import { DropFlow } from "./screens/DropFlow";
import { Reveal } from "./screens/Reveal";
import { Leaderboard } from "./screens/Leaderboard";
import { Commissioner } from "./screens/Commissioner";
import { Diagnostics } from "./screens/Diagnostics";
import { NewLeague } from "./screens/NewLeague";
import { Finale } from "./screens/Finale";
import { useState } from "react";
import { Opening, hasOpenedThisSession } from "./components/Opening";

function RequireAuth({ children }: { children: JSX.Element }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="app"><div className="loading">Loading…</div></div>;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return children;
}

export function App() {
  // The ritual runs once per app-open, over whatever screen you were heading
  // for, so a deep link from a notification still lands where it should.
  const [opening, setOpening] = useState(() => !hasOpenedThisSession());

  return (
    <>
      {opening && <Opening onDone={() => setOpening(false)} />}
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* Invite links are public until the visitor authenticates to accept. */}
      <Route path="/join/:token" element={<Join />} />

      {/* Device-level notification check. Deliberately outside the crew routes
          so it still works when someone is stuck and cannot load a crew page. */}
      <Route path="/diagnostics" element={<Diagnostics />} />

      {/* Starting a crew needs a host code, which NewLeague asks for. It sits
          outside the crew routes because it is not about a crew that exists. */}
      <Route path="/new-league" element={<RequireAuth><NewLeague /></RequireAuth>} />

      {/* No crew named: work out which one to open, then redirect. */}
      <Route path="/" element={<RequireAuth><Landing /></RequireAuth>} />

      {/* Every crew screen sits under one layout, so "which crew am I in, and
          am I actually in it" is answered once instead of per screen. Keeping
          the /league/ path leaves existing links and push deep links working;
          the word people see is "crew" everywhere in the copy. */}
      <Route path="/league/:leagueId" element={<RequireAuth><CrewLayout /></RequireAuth>}>
        {/* The everyday, inside the crew bar and the four tabs. */}
        <Route element={<TabbedLayout />}>
          <Route index element={<Home />} />
          <Route path="leaderboard" element={<Leaderboard />} />
          <Route path="checkin" element={<CheckIn />} />
          <Route path="candidates" element={<Candidates />} />
          <Route path="you" element={<You />} />
          <Route path="questions" element={<MyQuestions />} />
        </Route>

        {/* Bare on purpose. A drop is a clock running against you and a reveal
            is the payoff; neither wants a tab bar inviting you to leave in the
            middle. Onboarding and the host page are one-off destinations with
            their own way back. */}
        <Route path="onboarding" element={<Onboarding />} />
        <Route path="drop/:dropId" element={<DropFlow />} />
        <Route path="drop/:dropId/reveal" element={<Reveal />} />
        <Route path="finale" element={<Finale />} />
        <Route path="commissioner" element={<Commissioner />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}
